/**
 * 模板库跨页共享缓存：类目树 / 账号中心 / 站点列表。
 * 对齐 SHEIN 体感：进页先出列表，大 JSON 复用或延后加载。
 */

const CATEGORY_TTL_MS = 10 * 60 * 1000;
const ACCOUNT_TTL_MS = 5 * 60 * 1000;
const SITE_TTL_MS = 10 * 60 * 1000;

/** @type {Map<string, { data: any, at: number }>} */
const categoryByShop = new Map();
/** @type {Map<string, Promise<any>>} */
const categoryInflight = new Map();

/** @type {Map<string, { data: any, at: number }>} */
const siteListByShop = new Map();
/** @type {Map<string, Promise<any>>} */
const siteListInflight = new Map();

/** @type {{ workspaceMode: string, canAccessTeam: boolean, at: number } | null} */
let accountCenterCache = null;
/** @type {Promise<boolean> | null} */
let accountCenterInflight = null;
let accountCenterInflightMode = '';

function isFresh(at, ttl) {
  return Number.isFinite(at) && (Date.now() - at) < ttl;
}

export function peekShopCategories(shopId) {
  const key = String(shopId || '').trim();
  if (!key) return null;
  const hit = categoryByShop.get(key);
  if (!hit || !isFresh(hit.at, CATEGORY_TTL_MS)) return null;
  return hit.data;
}

export function rememberShopCategories(shopId, data) {
  const key = String(shopId || '').trim();
  if (!key) return;
  categoryByShop.set(key, { data, at: Date.now() });
}

export function invalidateShopCategories(shopId) {
  const key = String(shopId || '').trim();
  if (!key) {
    categoryByShop.clear();
    categoryInflight.clear();
    return;
  }
  categoryByShop.delete(key);
  categoryInflight.delete(key);
}

/**
 * @param {string} shopId
 * @param {() => Promise<any>} fetcher
 * @returns {Promise<any>}
 */
export function loadShopCategoriesCached(shopId, fetcher) {
  const key = String(shopId || '').trim();
  if (!key) return Promise.resolve([]);
  const cached = peekShopCategories(key);
  if (cached) return Promise.resolve(cached);
  const pending = categoryInflight.get(key);
  if (pending) return pending;
  const task = Promise.resolve()
    .then(() => fetcher())
    .then((res) => {
      const data = res?.categories ?? res?.items ?? res?.list ?? res ?? [];
      rememberShopCategories(key, data);
      return data;
    })
    .finally(() => {
      categoryInflight.delete(key);
    });
  categoryInflight.set(key, task);
  return task;
}

export function peekShopSiteList(shopId) {
  const key = String(shopId || '').trim();
  if (!key) return null;
  const hit = siteListByShop.get(key);
  if (!hit || !isFresh(hit.at, SITE_TTL_MS)) return null;
  return hit.data;
}

export function rememberShopSiteList(shopId, data) {
  const key = String(shopId || '').trim();
  if (!key) return;
  siteListByShop.set(key, { data, at: Date.now() });
}

export function invalidateShopSiteList(shopId) {
  const key = String(shopId || '').trim();
  if (!key) {
    siteListByShop.clear();
    siteListInflight.clear();
    return;
  }
  siteListByShop.delete(key);
  siteListInflight.delete(key);
}

/**
 * @param {string} shopId
 * @param {() => Promise<any>} fetcher
 */
export function loadShopSiteListCached(shopId, fetcher) {
  const key = String(shopId || '').trim();
  if (!key) return Promise.resolve(null);
  const cached = peekShopSiteList(key);
  if (cached != null) return Promise.resolve(cached);
  const pending = siteListInflight.get(key);
  if (pending) return pending;
  const task = Promise.resolve()
    .then(() => fetcher())
    .then((res) => {
      rememberShopSiteList(key, res);
      return res;
    })
    .finally(() => {
      siteListInflight.delete(key);
    });
  siteListInflight.set(key, task);
  return task;
}

export function peekCanAccessTeam(workspaceMode) {
  const mode = workspaceMode === 'team' ? 'team' : 'personal';
  if (!accountCenterCache || accountCenterCache.workspaceMode !== mode) return null;
  if (!isFresh(accountCenterCache.at, ACCOUNT_TTL_MS)) return null;
  return accountCenterCache.canAccessTeam;
}

/**
 * @param {string} workspaceMode
 * @param {() => Promise<boolean>} fetcher 返回 canAccessTeam
 */
export function loadCanAccessTeamCached(workspaceMode, fetcher) {
  const mode = workspaceMode === 'team' ? 'team' : 'personal';
  const cached = peekCanAccessTeam(mode);
  if (cached != null) return Promise.resolve(cached);
  if (accountCenterInflight && accountCenterInflightMode === mode) {
    return accountCenterInflight;
  }
  accountCenterInflightMode = mode;
  accountCenterInflight = Promise.resolve()
    .then(() => fetcher())
    .then((canAccessTeam) => {
      accountCenterCache = {
        workspaceMode: mode,
        canAccessTeam: Boolean(canAccessTeam),
        at: Date.now(),
      };
      return accountCenterCache.canAccessTeam;
    })
    .finally(() => {
      accountCenterInflight = null;
      accountCenterInflightMode = '';
    });
  return accountCenterInflight;
}

export function invalidateAccountCenterCache() {
  accountCenterCache = null;
  accountCenterInflight = null;
  accountCenterInflightMode = '';
}
